import { useMutation } from '@tanstack/react-query'

import { List } from '~/components/component-library'
import {
  Dialog,
  SplitsDialogContent,
} from '~/components/component-library/Dialog'
import { PasskeyConfirmationDialog } from '~/components/component-library/Dialog/PasskeyConfirmationDialog'
import { SignerIcon } from '~/components/smart-accounts/SignerIcon'
import { useAddNewPasskey } from '~/hooks/useAddNewPasskey'
import { useUserPasskeys } from '~/hooks/useUserPasskeys'
import { useAddPasskeyDialogStore } from '~/store/addPasskeyDialogStore'
import { PasskeyConfirmationAction } from '~/types'

// Mounted once beside GlobalModal and opened through addNewPasskey(). It has
// its own Radix root so it stacks over a signer picker that is already open
// and hands the new signer back to it.
export const AddPasskeyDialog = () => {
  const { resolve, step, close } = useAddPasskeyDialogStore()

  return (
    <Dialog
      open={resolve !== null}
      onOpenChange={(open) => {
        if (!open) close()
      }}
    >
      {step === 'confirm' ? <ConfirmStep /> : <CreateStep />}
    </Dialog>
  )
}

const ConfirmStep = () => {
  const { passkeys } = useUserPasskeys()
  const confirmed = useAddPasskeyDialogStore((s) => s.confirmed)

  return (
    <PasskeyConfirmationDialog
      id="addPasskeyConfirm"
      title="Confirm it's you"
      confirmText="Confirm"
      confirmation={{ action: PasskeyConfirmationAction.PasskeyRegister }}
      onConfirmed={async (confirmationId) => confirmed(confirmationId)}
    >
      <p>
        Adding a passkey is protected by the ones already on your account. Your
        device will ask you to sign in with one of these.
      </p>
      <List
        items={passkeys.map((passkey) => ({
          key: passkey.id,
          image: <SignerIcon signer={passkey} />,
          content: passkey.name,
        }))}
      />
    </PasskeyConfirmationDialog>
  )
}

const CreateStep = () => {
  const { confirmationId, close } = useAddPasskeyDialogStore()
  const { registerPasskey } = useAddNewPasskey()

  const { mutate: create, isPending } = useMutation({
    mutationFn: () => registerPasskey(confirmationId),
    onSuccess: (signer) => {
      if (signer) close(signer)
    },
  })

  return (
    <SplitsDialogContent
      id="addPasskeyCreate"
      title="Create your new passkey"
      variant="confirmation"
      isLoading={isPending}
      confirmButton={{ text: 'Create passkey', onClick: () => create() }}
    >
      <p>
        Your device will now ask you to create a passkey. Pick the device or key
        you want to add.
      </p>
    </SplitsDialogContent>
  )
}
